import { useCallback, useState } from "react";

import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { yupResolver } from "@hookform/resolvers/yup";

import { FORM_ERROR_DELAY_IN_MS } from "@covertree/consts";
import {
  EffectiveDateExposureAndLapseForm,
  effectiveDateExposureAndLapseValidations,
} from "@covertree/new-business-forms";
import {
  getDynamicQuestions,
  getPolicyUsage,
  selectFormDefaultValues,
  selectGeoCodeResult,
  selectIsBookConversion,
  selectOwnershipType,
  selectPolicyUsage,
  selectUnitDetails,
  setBasicPolicyDetails,
  setUnitsDetails,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { Toast } from "@covertree/utils";
import { UnitLocation } from "@covertree/new-business-graphql";

import { useAppDispatch } from "@redux/hooks";
import { navigateToPolicyStep, NewPolicyRoutes } from "@routing/paths";

import { EffectiveDateExposureAndLapseView } from "./EffectiveDateExposureAndLapseView";

export function EffectiveDateExposureAndLapse() {
  const navigate = useNavigate();

  const defaultValues = useNewBusinessSelector(selectFormDefaultValues);
  const { state } = useNewBusinessSelector(selectGeoCodeResult);
  const unitDetails = useNewBusinessSelector(selectUnitDetails);
  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);
  const policyUsage = useNewBusinessSelector(selectPolicyUsage);
  const ownershipType = useNewBusinessSelector(selectOwnershipType);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useAppDispatch();

  const form = useForm<EffectiveDateExposureAndLapseForm>({
    mode: "onChange",
    defaultValues,
    delayError: FORM_ERROR_DELAY_IN_MS,
    shouldUnregister: true,
    // TODO: Would love if someone would figure out that typescript issue - most likely discrepancy between strings in validation & enums in type
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resolver: yupResolver(
      effectiveDateExposureAndLapseValidations(isBookConversion, policyUsage)
    ),
    reValidateMode: "onChange",
  });

  // TODO: Add useScrollToErrorSection

  const onSubmit = useCallback(
    async (formValues: EffectiveDateExposureAndLapseForm) => {
      setIsSubmitting(true);
      dispatch(setBasicPolicyDetails(formValues));

      if (
        // TODO Change after mulit-unit is added
        unitDetails?.[0]?.details?.location === UnitLocation.OnMyOwnLand
      ) {
        dispatch(
          setUnitsDetails({
            unitIndex: 0,
            unitDetails: unitDetails[0].details,
          })
        );
      }

      const {
        meta: { requestStatus: requestStatusGetDynamicQuestions },
      } = await dispatch(
        getDynamicQuestions({
          effectiveDate: formValues.policyEffectiveDate!,
          policyUsage:
            policyUsage ??
            getPolicyUsage(
              ownershipType,
              formValues.residencyType,
              formValues.homeUsage!
            ),
          state,
        })
      );

      if (requestStatusGetDynamicQuestions === "rejected") {
        Toast.showError("Unexpected error while loading questions");
        setIsSubmitting(false);
        return;
      }

      navigateToPolicyStep(
        navigate,
        NewPolicyRoutes.HOME_DETAILS,
        NewPolicyRoutes.HOME_ADDRESS
      );

      setIsSubmitting(false);

      // TODO: Some weird Sequoia GeoCode stuff was here. I think it should be handled within HomeAddress.tsx component
    },
    [state, navigate, dispatch, unitDetails, policyUsage]
  );

  return (
    <EffectiveDateExposureAndLapseView
      form={form}
      onSubmit={onSubmit}
      loading={isSubmitting}
    />
  );
}
